/**
 * Shared locale preference for the marketing site and the portal.
 *
 * Explicit choice lives in localStorage (`obm-locale`) and a cookie (`obm_locale`) so
 * onbrandmail.com and app.onbrandmail.com can stay aligned when the cookie is visible.
 * Auto-detect from navigator.languages only when nothing is stored.
 */

export const LOCALES = ['en-AU', 'es', 'fr', 'de'] as const;

export type AppLocale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: AppLocale = 'en-AU';

export const LOCALE_STORAGE_KEY = 'obm-locale';
export const LOCALE_COOKIE_NAME = 'obm_locale';

export const LOCALE_LABELS: Record<AppLocale, string> = {
  'en-AU': 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
};

/** BCP 47 tags used by Intl / document.lang (en-AU stays en-AU). */
export function intlTag(locale: AppLocale): string {
  return locale;
}

export function isAppLocale(value: string | null | undefined): value is AppLocale {
  return value != null && (LOCALES as readonly string[]).includes(value);
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1] ?? '') : null;
}

function writeCookie(name: string, value: string): void {
  if (typeof document === 'undefined') {
    return;
  }
  const maxAge = 60 * 60 * 24 * 365;
  // Share preference between onbrandmail.com and app.onbrandmail.com in production.
  const host = window.location.hostname;
  const domain =
    host === 'onbrandmail.com' || host.endsWith('.onbrandmail.com')
      ? '; Domain=.onbrandmail.com'
      : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/${domain}; SameSite=Lax`;
}

/** Map browser tags (en, en-US, es-MX, …) onto our four app locales. */
export function mapBrowserLanguage(tag: string): AppLocale | null {
  const lower = tag.toLowerCase().replace('_', '-');
  if (lower.startsWith('en')) {
    return 'en-AU';
  }
  if (lower.startsWith('es')) {
    return 'es';
  }
  if (lower.startsWith('fr')) {
    return 'fr';
  }
  if (lower.startsWith('de')) {
    return 'de';
  }
  return null;
}

export function detectBrowserLocale(): AppLocale {
  if (typeof navigator === 'undefined') {
    return DEFAULT_LOCALE;
  }
  const candidates = [...(navigator.languages ?? []), navigator.language].filter(Boolean);
  for (const tag of candidates) {
    const mapped = mapBrowserLanguage(tag);
    if (mapped) {
      return mapped;
    }
  }
  return DEFAULT_LOCALE;
}

export function readStoredLocale(): AppLocale | null {
  try {
    const fromStorage = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isAppLocale(fromStorage)) {
      return fromStorage;
    }
  } catch {
    /* private mode */
  }
  const fromCookie = readCookie(LOCALE_COOKIE_NAME);
  if (isAppLocale(fromCookie)) {
    return fromCookie;
  }
  return null;
}

export function persistLocale(locale: AppLocale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* private mode */
  }
  writeCookie(LOCALE_COOKIE_NAME, locale);
}

/** Preference if set, otherwise browser detection. */
export function resolveLocale(): AppLocale {
  return readStoredLocale() ?? detectBrowserLocale();
}

export function applyDocumentLang(locale: AppLocale): void {
  if (typeof document === 'undefined') {
    return;
  }
  document.documentElement.lang = locale;
}
